import { getCustomerRegistryTypeFromCustomer, normalizeCustomerGender, requiredCustomerFields, validItalianFiscalCode as validRegistryFiscalCode } from "./customerRegistryTypes";
import { parseVestaDate } from "./dateInput";
const PHONE_TYPES = new Set(["telefono", "cellulare", "fax"]);
const REQUIRED_PHONE_TYPES = new Set(["telefono", "cellulare"]);
const EMAIL_TYPES = new Set(["email", "pec"]);
const BOOKING_STATUSES = new Set(["PENDING", "CONFIRMED", "CHECKED_IN", "CHECKED_OUT", "CANCELLED"]);
const EU_COUNTRY_ISO2 = new Set(["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"]);
const FOREIGN_PRIVATE_TAX_ID = "0000000";
const FOREIGN_EXTRA_EU_NO_TAX_ID = "99999999999";

function text(value) {
  return String(value ?? "").trim();
}

function first(source = {}, ...keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && text(value) !== "") return value;
  }
  return "";
}

function setError(errors, key, message) {
  if (!errors[key]) errors[key] = message;
}

function parseDate(value) {
  return parseVestaDate(value);
}

function isItaly(value) {
  const token = text(value).toLocaleLowerCase("it");
  return ["italia", "italy", "it"].includes(token);
}

function validEmail(value) {
  const email = text(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);
}

function phoneDigits(value) {
  return text(value).replace(/[^0-9]/g, "");
}

function validItalianVat(value) {
  const vat = text(value);
  if (!/^\d{11}$/.test(vat)) return false;
  let sum = 0;
  for (let index = 0; index < 10; index += 1) {
    let digit = Number(vat[index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return (10 - (sum % 10)) % 10 === Number(vat[10]);
}

function validLuhn(value) {
  const digits = text(value).replace(/\D/g, "");
  if (digits.length < 12 || digits.length > 19) return false;
  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

export function inferCustomerEntityType(customer = {}, customerTypeCatalog = []) {
  const type = getCustomerRegistryTypeFromCustomer(customer, customerTypeCatalog);
  if (!type) return "";
  return type.personDataRequired ? "PERSON" : type.entityType;
}

export function isPersonCustomer(customer = {}, customerTypeCatalog = []) {
  return Boolean(getCustomerRegistryTypeFromCustomer(customer, customerTypeCatalog)?.personDataRequired);
}

function countryIso2(items = [], value = "") {
  const wanted = text(value).toLocaleLowerCase("it");
  if (!wanted) return "";
  const match = (Array.isArray(items) ? items : []).find((item) => text(item?.description).toLocaleLowerCase("it") === wanted || text(item?.iso2).toLocaleLowerCase("it") === wanted);
  if (match?.iso2) return text(match.iso2).toUpperCase().slice(0, 2);
  return isItaly(value) ? "IT" : "";
}

export function validateCustomerRegistry(source = {}, { customerTypeCatalog = [], municipalities = [], countries = [], requiredPreferences = {} } = {}) {
  const errors = {};
  const country = text(first(source, "country", "nazione"));
  const registryType = getCustomerRegistryTypeFromCustomer(source, customerTypeCatalog);
  const required = requiredCustomerFields(registryType?.code || first(source, "customerTypeId", "customerType", "tipoAnagraficaId", "tipoAnagrafica"), country);
  const businessName = text(first(source, "businessName", "ragioneSociale"));
  const name = text(first(source, "name", "nome"));
  const surname = text(first(source, "surname", "cognome"));
  const fiscalCode = text(first(source, "fiscalCode", "codiceFiscale")).toUpperCase();
  const vatNumber = text(first(source, "vatNumber", "partitaIva"));
  const sdiCode = text(first(source, "sdiCode", "codiceSdi")).toUpperCase();
  const gender = normalizeCustomerGender(first(source, "gender", "sesso"));
  const birthDateValue = text(first(source, "birthDate", "dataNascita"));
  const birthCountry = text(first(source, "birthCountry", "statoNascita"));
  const birthCity = text(first(source, "birthCity", "comuneNascita"));
  const birthProvince = text(first(source, "birthProvince", "provinciaNascita")).toUpperCase();
  const nationality = text(first(source, "nationality", "cittadinanza"));
  const province = text(first(source, "province", "provincia")).toUpperCase();
  const postalCode = text(first(source, "postalCode", "cap"));

  if (!registryType) setError(errors, "customerType", "Seleziona un tipo di anagrafica valido.");
  if (!country) setError(errors, "country", "Seleziona la nazione.");
  if (required.has("businessName") && !businessName) setError(errors, "businessName", "La ragione sociale è obbligatoria.");
  else if (businessName.length > 160) setError(errors, "businessName", "Massimo 160 caratteri.");

  if (required.has("name") && !name) setError(errors, "name", "Il nome è obbligatorio per questa tipologia.");
  if (required.has("surname") && !surname) setError(errors, "surname", "Il cognome è obbligatorio per questa tipologia.");
  if (required.has("gender") && !gender) setError(errors, "gender", "Seleziona il sesso.");
  if (required.has("birthDate") && !birthDateValue) setError(errors, "birthDate", "La data di nascita è obbligatoria.");
  if (required.has("birthCountry") && !birthCountry) setError(errors, "birthCountry", "Seleziona lo Stato di nascita.");
  if (required.has("birthCity") && !birthCity) setError(errors, "birthCity", "Seleziona il Comune di nascita.");
  if (required.has("nationality") && !nationality) setError(errors, "nationality", "Seleziona la cittadinanza.");
  if (required.has("fiscalCode") && !fiscalCode) setError(errors, "fiscalCode", "Il Codice Fiscale è obbligatorio per questa tipologia.");
  if (required.has("vatNumber") && !vatNumber) setError(errors, "vatNumber", isItaly(country) ? "La Partita IVA è obbligatoria per questa tipologia." : "L'Identificativo Fiscale è obbligatorio per questa tipologia estera.");
  if (required.has("sdiCode") && !sdiCode) setError(errors, "sdiCode", registryType?.sdiLength === 6 ? "Il Codice Univoco Ufficio è obbligatorio per la Pubblica Amministrazione." : "Il Codice SDI è obbligatorio per questa tipologia.");

  if (name.length > 80) setError(errors, "name", "Massimo 80 caratteri.");
  if (surname.length > 80) setError(errors, "surname", "Massimo 80 caratteri.");
  if (gender && !["M", "F"].includes(gender)) setError(errors, "gender", "Valore non valido.");
  if (fiscalCode && isItaly(country)) {
    const personType = Boolean(registryType?.personDataRequired);
    const validFiscalCode = personType ? validRegistryFiscalCode(fiscalCode) : (/^\d{11}$/.test(fiscalCode) ? validItalianVat(fiscalCode) : validRegistryFiscalCode(fiscalCode));
    if (!validFiscalCode) setError(errors, "fiscalCode", personType ? "Codice Fiscale italiano non valido: verifica i 16 caratteri e il codice di controllo." : "Codice Fiscale italiano non valido.");
  }
  if (vatNumber && isItaly(country) && !validItalianVat(vatNumber)) setError(errors, "vatNumber", "Partita IVA italiana non valida: servono 11 cifre e un codice di controllo corretto.");
  if (vatNumber && country && !isItaly(country) && !/^[A-Z0-9]{1,28}$/i.test(vatNumber)) setError(errors, "vatNumber", "L'Identificativo Fiscale estero deve essere alfanumerico e contenere al massimo 28 caratteri.");
  if (vatNumber && country && !isItaly(country)) {
    const iso2 = countryIso2(countries, country);
    if (registryType?.code === "PRIVATO" && vatNumber !== FOREIGN_PRIVATE_TAX_ID) {
      setError(errors, "vatNumber", `Per un Privato estero l'Identificativo Fiscale deve essere ${FOREIGN_PRIVATE_TAX_ID}.`);
    } else if (registryType?.code !== "PRIVATO" && vatNumber === FOREIGN_PRIVATE_TAX_ID) {
      setError(errors, "vatNumber", `${FOREIGN_PRIVATE_TAX_ID} è riservato ai Privati esteri.`);
    } else if (vatNumber === FOREIGN_EXTRA_EU_NO_TAX_ID && (!iso2 || EU_COUNTRY_ISO2.has(iso2))) {
      setError(errors, "vatNumber", `${FOREIGN_EXTRA_EU_NO_TAX_ID} può essere usato solo per soggetti extra-UE senza Partita IVA / Identificativo Fiscale estero.`);
    }
  }
  if (sdiCode) {
    const expectedLength = isItaly(country) ? (registryType?.sdiLength || 7) : 7;
    if (!new RegExp(`^[A-Z0-9]{${expectedLength}}$`).test(sdiCode)) setError(errors, "sdiCode", expectedLength === 6 ? "Il Codice Univoco Ufficio deve contenere esattamente 6 caratteri alfanumerici." : "Il Codice SDI deve contenere esattamente 7 caratteri alfanumerici.");
  }
  if (province && isItaly(country) && !/^[A-Z]{2}$/.test(province)) setError(errors, "province", "La sigla provincia deve contenere esattamente 2 lettere.");
  if (birthProvince && isItaly(birthCountry) && !/^[A-Z]{2}$/.test(birthProvince)) setError(errors, "birthProvince", "La sigla provincia di nascita deve contenere esattamente 2 lettere.");
  if (postalCode && isItaly(country) && !/^\d{5}$/.test(postalCode)) setError(errors, "postalCode", "Il CAP italiano deve contenere esattamente 5 cifre.");

  if (birthDateValue) {
    const birthDate = parseDate(birthDateValue);
    if (!birthDate) setError(errors, "birthDate", "Data di nascita non valida.");
    else {
      const today = new Date();
      const oldest = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
      if (birthDate > today) setError(errors, "birthDate", "La data di nascita non può essere futura.");
      else if (birthDate < oldest) setError(errors, "birthDate", "La data di nascita supera il limite di 120 anni.");
    }
  }

  const contacts = Array.isArray(source.contacts) ? source.contacts : [];
  const seenContacts = new Set();
  const primaryContactsByType = new Map();
  contacts.forEach((contact, index) => {
    const type = text(contact?.contactType || contact?.tipoContatto);
    const value = text(contact?.contactValue || contact?.datoContatto);
    const typeKey = type.toLocaleLowerCase("it");
    if (!type) setError(errors, `contacts.${index}.contactType`, "Seleziona il tipo di contatto.");
    if (!value) setError(errors, `contacts.${index}.contactValue`, "Inserisci il contatto.");
    if (REQUIRED_PHONE_TYPES.has(typeKey) && !text(contact?.countryPrefixId)) setError(errors, `contacts.${index}.countryPrefixId`, "Seleziona il prefisso internazionale.");
    if (PHONE_TYPES.has(typeKey) && value) {
      const digits = phoneDigits(value);
      if (REQUIRED_PHONE_TYPES.has(typeKey) && !/^\d+$/.test(value)) setError(errors, `contacts.${index}.contactValue`, "Telefono e Cellulare possono contenere solo numeri.");
      else if (digits.length < 5 || digits.length > 15) setError(errors, `contacts.${index}.contactValue`, "Il numero deve contenere da 5 a 15 cifre.");
    }
    if (EMAIL_TYPES.has(typeKey) && value && !validEmail(value)) setError(errors, `contacts.${index}.contactValue`, "Indirizzo email non valido.");
    const interlocutor = text(contact?.interlocutor || contact?.interlocutore);
    if (interlocutor && !/^[\p{L}]+(?:\s+[\p{L}]+)*$/u.test(interlocutor)) setError(errors, `contacts.${index}.interlocutor`, "L'interlocutore può contenere solo lettere.");
    const duplicateKey = `${typeKey}|${value.toLocaleLowerCase("it")}`;
    if (type && value && seenContacts.has(duplicateKey)) setError(errors, `contacts.${index}.contactValue`, "Questo contatto è già presente.");
    if (type && value) seenContacts.add(duplicateKey);
    if (contact?.primary === true || contact?.principale === true) {
      const primaryKey = typeKey || `__row_${index}`;
      primaryContactsByType.set(primaryKey, (primaryContactsByType.get(primaryKey) || 0) + 1);
    }
  });
  const hasRequiredPhoneContact = contacts.some((contact) => {
    const typeKey = text(contact?.contactType || contact?.tipoContatto).toLocaleLowerCase("it");
    const value = text(contact?.contactValue || contact?.datoContatto);
    return REQUIRED_PHONE_TYPES.has(typeKey) && Boolean(value);
  });
  if (!hasRequiredPhoneContact) {
    const phoneRowIndex = contacts.findIndex((contact) => REQUIRED_PHONE_TYPES.has(text(contact?.contactType || contact?.tipoContatto).toLocaleLowerCase("it")));
    setError(errors, `contacts.${phoneRowIndex >= 0 ? phoneRowIndex : 0}.${phoneRowIndex >= 0 ? "contactValue" : "contactType"}`, "È obbligatorio almeno un contatto Telefono o Cellulare.");
  }

  contacts.forEach((contact, index) => {
    const typeKey = text(contact?.contactType || contact?.tipoContatto).toLocaleLowerCase("it");
    if ((contact?.primary === true || contact?.principale === true) && typeKey && (primaryContactsByType.get(typeKey) || 0) > 1) {
      setError(errors, `contacts.${index}.primary`, "Può esserci un solo contatto principale per ogni tipo.");
    }
  });

  const genericSdiCode = isItaly(country) && (
    (["DITTA_INDIVIDUALE", "DITTA_SOCIETA"].includes(registryType?.code) && sdiCode === "0000000")
    || (registryType?.code === "PUBBLICA_AMMINISTRAZIONE" && sdiCode === "000000")
  );
  if (genericSdiCode) {
    const hasValidPec = contacts.some((contact) => text(contact?.contactType || contact?.tipoContatto).toLocaleLowerCase("it") === "pec" && validEmail(contact?.contactValue || contact?.datoContatto));
    if (!hasValidPec) setError(errors, "sdiCode", `Con il codice ${sdiCode} è obbligatorio inserire una PEC valida nella sezione Contatti.`);
  }

  const documents = Array.isArray(source.documents) ? source.documents : [];
  const seenDocumentTypes = new Set();
  let primaryDocuments = 0;
  if (documents.length === 0) setError(errors, "documents.0.documentType", "È obbligatorio inserire almeno un documento.");
  documents.forEach((document, index) => {
    const type = text(document?.documentType || document?.documentoTipo);
    const number = text(document?.documentNumber || document?.documentoNumero);
    if (!type) setError(errors, `documents.${index}.documentType`, "Seleziona il tipo di documento.");
    if (!number) setError(errors, `documents.${index}.documentNumber`, "Inserisci il numero del documento.");
    if (number && !/^[A-Z0-9]+$/.test(number)) setError(errors, `documents.${index}.documentNumber`, "Il numero documento può contenere solo lettere maiuscole e numeri, senza spazi.");
    if (number.length > 40) setError(errors, `documents.${index}.documentNumber`, "Massimo 40 caratteri.");
    const issuer = text(document?.documentIssuer || document?.enteRilascioDocumento);
    if (issuer && !/^[\p{L}\p{N} ]+$/u.test(issuer)) setError(errors, `documents.${index}.documentIssuer`, "L'ente rilascio può contenere solo lettere, numeri e spazi.");
    const issueDateValue = text(document?.documentIssueDate || document?.dataRilascioDocumento);
    const expiryDateValue = text(document?.documentExpiryDate || document?.dataScadenzaDocumento);
    const issueDate = issueDateValue ? parseDate(issueDateValue) : null;
    const expiryDate = expiryDateValue ? parseDate(expiryDateValue) : null;
    if (issueDateValue && !issueDate) setError(errors, `documents.${index}.documentIssueDate`, "Data di rilascio non valida.");
    if (expiryDateValue && !expiryDate) setError(errors, `documents.${index}.documentExpiryDate`, "Data di scadenza non valida.");
    if (issueDate && issueDate > new Date()) setError(errors, `documents.${index}.documentIssueDate`, "La data di rilascio non può essere futura.");
    if (issueDate && expiryDate && expiryDate < issueDate) setError(errors, `documents.${index}.documentExpiryDate`, "La scadenza non può precedere il rilascio.");
    const documentTypeKey = type.toLocaleLowerCase("it");
    if (type && seenDocumentTypes.has(documentTypeKey)) setError(errors, `documents.${index}.documentType`, "Questa tipologia di documento è già presente.");
    if (type) seenDocumentTypes.add(documentTypeKey);
    if (document?.documentIsPrimary === true || document?.documentoPrincipale === true) primaryDocuments += 1;
  });
  if (primaryDocuments > 1) {
    documents.forEach((document, index) => {
      if (document?.documentIsPrimary === true || document?.documentoPrincipale === true) setError(errors, `documents.${index}.documentIsPrimary`, "Può esserci un solo documento principale.");
    });
  }

  [
    ["preferredLanguage", "Lingua preferita obbligatoria."],
    ["preferredRoomTypeId", "Tipo camera preferita obbligatorio."],
    ["preferredFloor", "Piano preferito obbligatorio."],
    ["preferredBedTypeId", "Tipo letto preferito obbligatorio."],
    ["arrivalPreference", "Preferenza arrivo obbligatoria."],
  ].forEach(([field, message]) => {
    if (requiredPreferences?.[field] === true && !text(source?.[field])) setError(errors, field, message);
  });

  [["privacyConsent", "privacyConsentDate", "privacy"], ["marketingConsent", "marketingConsentDate", "marketing"], ["profilingConsent", "profilingConsentDate", "profilazione"]].forEach(([flag, dateField, label]) => {
    if (source?.[flag] !== true) setError(errors, flag, `Il consenso ${label} è obbligatorio.`);
    if (!text(source?.[dateField])) setError(errors, dateField, `Data consenso ${label} obbligatoria.`);
    else if (!parseDate(source?.[dateField])) setError(errors, dateField, "Data non valida.");
  });
  const status = text(first(source, "customerStatus", "statoCliente")).toUpperCase();
  if (status === "BLOCKED" && !text(first(source, "blockedReason", "motivoBlocco"))) setError(errors, "blockedReason", "Indica il motivo del blocco.");
  if (source?.touristTaxExemptionDefault === true && !text(first(source, "touristTaxExemptionReason", "motivoEsenzioneImposta"))) setError(errors, "touristTaxExemptionReason", "Indica il motivo dell'esenzione.");

  const refs = Array.isArray(source.externalReferences) ? source.externalReferences : [];
  refs.forEach((reference, index) => {
    const refSource = text(reference?.source);
    const externalId = text(reference?.externalId);
    if (!refSource) setError(errors, `externalReferences.${index}.source`, "Indica l'origine del riferimento.");
    if (!externalId) setError(errors, `externalReferences.${index}.externalId`, "Inserisci l'ID esterno.");
  });

  return errors;
}

export function validateBookingRegistry(payload = {}, { draft = {}, customers = [], customerTypeCatalog = [], mode = "" } = {}) {
  const errors = {};
  const customerId = text(payload.customer_id);
  const selectedCustomer = (customers || []).find((customer) => String(customer?._id || customer?.id || "") === customerId);

  if (!customerId || !selectedCustomer) setError(errors, "guest_name", "Seleziona un'anagrafica cliente valida.");
  else if (!isPersonCustomer(selectedCustomer, customerTypeCatalog)) setError(errors, "guest_name", "Nella prenotazione il cliente deve essere una persona, non un'azienda.");
  else if (selectedCustomer.isBlocked === true || text(selectedCustomer.customerStatus).toUpperCase() === "BLOCKED") setError(errors, "guest_name", "Il cliente selezionato è bloccato.");

  if (payload.invoice_same_as_customer === false) {
    const invoiceId = text(payload.invoice_customer_id);
    if (!invoiceId || !(customers || []).some((customer) => String(customer?._id || customer?.id || "") === invoiceId)) setError(errors, "invoice_name", "Seleziona un intestatario fattura valido.");
    if (text(payload.invoice_email) && !validEmail(payload.invoice_email)) setError(errors, "invoice_email", "Indirizzo email non valido.");
    if (text(payload.invoice_phone)) {
      const digits = phoneDigits(payload.invoice_phone);
      if (digits.length < 5 || digits.length > 15) setError(errors, "invoice_phone", "Il numero deve contenere da 5 a 15 cifre.");
    }
  }

  if (mode !== "customer") {
    if (!text(payload.room_number)) setError(errors, "room_number", "Seleziona la camera.");
    if (!BOOKING_STATUSES.has(text(payload.status).toUpperCase())) setError(errors, "status", "Seleziona uno stato prenotazione valido.");
    const checkIn = payload.check_in ? new Date(payload.check_in) : parseDate(first(draft, "check_in"));
    const checkOut = payload.check_out ? new Date(payload.check_out) : parseDate(first(draft, "check_out"));
    if (!checkIn || Number.isNaN(checkIn.getTime())) setError(errors, "check_in", "Inserisci una data di Check In valida.");
    if (!checkOut || Number.isNaN(checkOut.getTime())) setError(errors, "check_out", "Inserisci una data di Check Out valida.");
    if (checkIn && checkOut && checkOut <= checkIn) setError(errors, "check_out", "Il Check Out deve essere successivo al Check In.");
  }

  const guests = Array.isArray(draft.relatedGuests) ? draft.relatedGuests : [];
  const seen = new Set();
  guests.forEach((guest, index) => {
    const role = text(guest?.relationshipType || guest?.guestRole).toUpperCase();
    if (!role) setError(errors, `relatedGuests.${index}.relationshipType`, "Seleziona il tipo ospite.");
    if (role === "ANIMAL") return;
    const id = text(guest?.customer_id || guest?.customerId);
    const customer = (customers || []).find((item) => String(item?._id || item?.id || "") === id);
    if (!id || !customer) setError(errors, `relatedGuests.${index}.customer_id`, "Seleziona un'anagrafica persona valida.");
    else if (!isPersonCustomer(customer, customerTypeCatalog)) setError(errors, `relatedGuests.${index}.customer_id`, "Un ospite correlato deve essere una persona, non un'azienda.");
    else if (customer.isBlocked === true || text(customer.customerStatus).toUpperCase() === "BLOCKED") setError(errors, `relatedGuests.${index}.customer_id`, "L'ospite selezionato è bloccato.");
    if (id && id === customerId) setError(errors, `relatedGuests.${index}.customer_id`, "Il cliente principale non può essere duplicato tra gli ospiti correlati.");
    if (id && seen.has(id)) setError(errors, `relatedGuests.${index}.customer_id`, "Lo stesso ospite è già presente nella prenotazione.");
    if (id) seen.add(id);
  });

  const payments = Array.isArray(payload.payments) ? payload.payments : [];
  payments.forEach((payment, index) => {
    if (!text(payment?.paymentMethod)) setError(errors, `payments.${index}.paymentMethod`, "Seleziona la condizione di pagamento.");
    const amount = Number(payment?.amount);
    if (!Number.isFinite(amount) || amount <= 0) setError(errors, `payments.${index}.amount`, "L'importo deve essere maggiore di zero.");
    if (text(payment?.notes).length > 250) setError(errors, `payments.${index}.notes`, "Massimo 250 caratteri.");
  });
  const total = Number(payload.total_amount || 0);
  const deposits = payments.filter((payment) => ["ADVANCE", "DEPOSIT"].includes(String(payment?.paymentType || (payment?.isDeposit === true ? "ADVANCE" : "PAYMENT")).toUpperCase())).reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
  if (Number.isFinite(total) && total >= 0 && deposits > total && total > 0) {
    payments.forEach((payment, index) => {
      if (["ADVANCE", "DEPOSIT"].includes(String(payment?.paymentType || (payment?.isDeposit === true ? "ADVANCE" : "PAYMENT")).toUpperCase())) setError(errors, `payments.${index}.amount`, "La somma di Acconti e Caparre non può superare il totale della prenotazione.");
    });
  }

  if (mode === "checkin") {
    if (!text(payload.document_type)) setError(errors, "document_type", "Seleziona il documento.");
    if (!text(payload.document_number)) setError(errors, "document_number", "Inserisci il numero del documento.");
    if (!text(payload.payment_method)) setError(errors, "payment_method", "Seleziona il metodo di pagamento.");
    if (!Number.isFinite(total) || total < 0) setError(errors, "total_amount", "Il totale non è valido.");
    const cardNumber = text(payload.credit_card_number).replace(/\D/g, "");
    const circuit = text(payload.credit_card_circuit_id || payload.credit_card);
    const expiry = text(payload.credit_card_expiry);
    if (cardNumber || circuit || expiry) {
      if (!cardNumber || !validLuhn(cardNumber)) setError(errors, "credit_card_number", "Numero carta non valido.");
      if (!circuit) setError(errors, "credit_card_circuit_id", "Seleziona il circuito della carta.");
      if (!/^\d{2}\/\d{4}$/.test(expiry)) setError(errors, "credit_card_expiry", "Scadenza non valida: usa mm/aaaa.");
      else {
        const [month, year] = expiry.split("/").map(Number);
        const expiryDate = new Date(year, month, 0, 23, 59, 59);
        if (month < 1 || month > 12 || expiryDate < new Date()) setError(errors, "credit_card_expiry", "La carta risulta scaduta.");
      }
    }
  }

  return errors;
}

export function firstValidationKey(errors = {}) {
  return Object.keys(errors)[0] || "";
}
